import { createFileRoute, Link } from "@tanstack/react-router";
import { magazineTopics } from "@/data/magazine-topics";
import { useTranslation } from "react-i18next";
import { detectInitialLocale } from "@/lib/i18n";

export const Route = createFileRoute("/magazin/")({
  head: () => {
    const locale = detectInitialLocale();
    const isEn = locale === "en";
    const title = isEn ? "Magazine, OM Selected" : "Magazin, OM Selected";
    const description = isEn
      ? "Curated guides for photography: portrait, wedding, product, fashion and editorial. Tips for amateur models and insights for clients."
      : "Kuratierte Ratgeber für Fotografie: Portrait, Hochzeit, Produkt, Fashion und Editorial. Tipps für Amateur-Models und Insights für Kunden.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
      ],
    };
  },
  component: MagazineIndexPage,
});

function MagazineIndexPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const tt = isEn
    ? {
        eyebrow: "Magazine",
        title: "Guides, stories and insights",
        intro:
          "Curated articles on photography genres, practical tips and inspiration for clients and creatives.",
        tipsLabel: "Tips for amateur models",
        tipsDesc:
          "What to look for in test shoots, castings and your first collaborations with photographers and make-up artists.",
        redflagLabel: "Red-flag questions",
        redflagDesc:
          "Six questions to ask before booking a photographer or make-up artist, so you can spot warning signs early.",
        pricingLabel: "How much does a photographer cost?",
        pricingDesc:
          "Typical price ranges, what drives the cost and how to compare quotes fairly before booking.",
      }
    : {
        eyebrow: "Magazin",
        title: "Ratgeber, Geschichten und Insights",
        intro:
          "Kuratierte Artikel zu Fotografie-Genres, praktische Tipps und Inspiration für Kunden und Kreative.",
        tipsLabel: "Tipps für Amateur-Models",
        tipsDesc:
          "Worauf du bei Testshootings, Castings und deinen ersten Zusammenarbeiten mit Fotograf:innen und Visagist:innen achten solltest.",
        redflagLabel: "Red-Flag-Fragen",
        redflagDesc:
          "Sechs Fragen, die du vor der Buchung von Fotograf:in oder Visagist:in stellen solltest, um Warnsignale früh zu erkennen.",
        pricingLabel: "Wie viel kostet ein Fotograf?",
        pricingDesc:
          "Typische Preisbereiche, was den Preis beeinflusst und wie du Angebote vor der Buchung fair vergleichst.",
      };

  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] pt-12 md:pt-20 pb-16 md:pb-24">
          <span className="eyebrow text-muted-foreground block mb-6">{tt.eyebrow}</span>
          <h1 className="serif text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
            {tt.title}
          </h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-2xl leading-snug text-muted-foreground">
            {tt.intro}
          </p>
        </div>
      </section>

      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[1280px] py-16 md:py-24">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {magazineTopics.map((topic) => {
              const label = isEn ? topic.labelEn : topic.label;
              const title = isEn ? topic.titleEn : topic.title;
              const metaDesc = isEn ? topic.metaDescriptionEn : topic.metaDescription;
              const slug = isEn ? topic.slugEn : topic.slug;
              return (
                <Link
                  key={topic.slug}
                  to="/magazin/$topic"
                  params={{ topic: slug }}
                  className="group block border border-border p-6 md:p-8 hover:bg-muted/40 transition-colors"
                >
                  <span className="eyebrow text-muted-foreground text-xs uppercase tracking-widest">
                    {label}
                  </span>
                  <h2 className="serif text-xl md:text-2xl mt-3 group-hover:underline">
                    {title}
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {metaDesc}
                  </p>
                </Link>
              );
            })}
            <Link
              to="/magazin/tipps-amateur-models"
              className="group block border border-border p-6 md:p-8 hover:bg-muted/40 transition-colors"
            >
              <span className="eyebrow text-muted-foreground text-xs uppercase tracking-widest">
                {tt.eyebrow}
              </span>
              <h2 className="serif text-xl md:text-2xl mt-3 group-hover:underline">
                {tt.tipsLabel}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {tt.tipsDesc}
              </p>
            </Link>
            <Link
              to="/magazin/red-flag-fragen"
              className="group block border border-border p-6 md:p-8 hover:bg-muted/40 transition-colors"
            >
              <span className="eyebrow text-muted-foreground text-xs uppercase tracking-widest">
                {isEn ? "Before you book" : "Vor der Buchung"}
              </span>
              <h2 className="serif text-xl md:text-2xl mt-3 group-hover:underline">
                {tt.redflagLabel}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {tt.redflagDesc}
              </p>
            </Link>
            <Link
              to="/magazin/wie-viel-kostet-ein-fotograf"
              className="group block border border-border p-6 md:p-8 hover:bg-muted/40 transition-colors"
            >
              <span className="eyebrow text-muted-foreground text-xs uppercase tracking-widest">
                {isEn ? "Pricing" : "Preise"}
              </span>
              <h2 className="serif text-xl md:text-2xl mt-3 group-hover:underline">
                {tt.pricingLabel}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {tt.pricingDesc}
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
